import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, ChevronRight, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Renders the currently-active "Daily Special" featured bet.
 *
 * Polls every 60s so it disappears automatically when kickoff hits or
 * admin deactivates. Returns null when no special is live — never leaves
 * dead space on the page.
 *
 * Used by both Lobby and Sportsbook.
 */
const DailySpecialBanner = ({ compact = false, testId = 'daily-special-banner' }) => {
    const { t } = useTranslation();
    const [special, setSpecial] = useState(null);
    const [countdown, setCountdown] = useState('');
    const navigate = useNavigate();

    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/daily-special/active`);
            setSpecial(data?.special || null);
        } catch {
            setSpecial(null);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, []);

    // Tick countdown every second
    useEffect(() => {
        if (!special?.commence_time) { setCountdown(''); return undefined; }
        const tick = () => {
            const ms = new Date(special.commence_time).getTime() - Date.now();
            if (ms <= 0) { setCountdown(t('daily_special.live_now')); return; }
            const mins = Math.floor(ms / 60_000);
            if (mins < 60) { setCountdown(t('daily_special.kickoff_in', { when: `${mins}m` })); return; }
            const hrs = Math.floor(mins / 60);
            const m = mins % 60;
            if (hrs < 24) { setCountdown(t('daily_special.kickoff_in', { when: `${hrs}h ${m}m` })); return; }
            const days = Math.floor(hrs / 24);
            setCountdown(t('daily_special.kickoff_in', { when: `${days}d` }));
        };
        tick();
        const id = setInterval(tick, 30_000);
        return () => clearInterval(id);
    }, [special?.commence_time]);

    if (!special) return null;

    const oddsLabel = special.odds > 0 ? `+${special.odds}` : `${special.odds}`;
    const subhead = (() => {
        const m = (special.market || 'h2h').toLowerCase();
        if (m === 'spreads' && special.point != null) {
            const pt = special.point > 0 ? `+${special.point}` : `${special.point}`;
            return `${special.outcome_name} ${pt}`;
        }
        if (m === 'totals' && special.point != null) {
            return `${special.outcome_name} ${special.point}`;
        }
        return special.outcome_name;
    })();

    const onClick = () => {
        // Deep-link straight to the bet in the slip — Sportsbook reads
        // these params and auto-selects sport + event + side.
        const qs = new URLSearchParams({
            special: special.id,
            event: special.event_id,
            sport: special.sport_key,
            market: special.market,
            outcome: special.outcome_name,
            ...(special.point != null ? { point: String(special.point) } : {}),
        }).toString();
        navigate(`/sportsbook?${qs}`);
    };

    return (
        <AnimatePresence>
            <motion.button
                key={special.id}
                type="button"
                onClick={onClick}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className={`relative w-full overflow-hidden text-left rounded-2xl border ${compact ? 'p-3' : 'p-4 sm:p-5'}`}
                style={{
                    background: 'linear-gradient(135deg, rgba(255,59,48,0.18) 0%, rgba(255,140,0,0.12) 50%, rgba(255,215,0,0.10) 100%)',
                    borderColor: 'rgba(255,140,0,0.45)',
                }}
                data-testid={testId}
            >
                {/* Pulsing glow on the left edge */}
                <motion.div
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 w-2"
                    style={{ background: 'linear-gradient(180deg, #FF3B30, #FF8C00)' }}
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                />

                <div className="relative pl-3 flex items-center gap-3 sm:gap-4">
                    {/* Flame icon */}
                    <motion.div
                        animate={{ rotate: [-4, 4, -4] }}
                        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                        className={`shrink-0 ${compact ? 'w-9 h-9' : 'w-12 h-12'} rounded-full bg-[#FF3B30]/20 border border-[#FF8C00]/60 flex items-center justify-center`}
                    >
                        <Flame className={`${compact ? 'w-4 h-4' : 'w-6 h-6'} text-[#FF8C00]`} />
                    </motion.div>

                    {/* Pitch + matchup */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-[#FF8C00] bg-[#FF8C00]/15 border border-[#FF8C00]/40 px-2 py-0.5 rounded-full">
                                {t('daily_special.label')}
                            </span>
                            {countdown && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-[#A0AAB2]">
                                    <Clock className="w-3 h-3" />
                                    {countdown}
                                </span>
                            )}
                        </div>
                        <p className={`text-white font-black ${compact ? 'text-sm' : 'text-base sm:text-lg'} leading-tight truncate`}>
                            {special.pitch}
                        </p>
                        <p className={`text-[#A0AAB2] ${compact ? 'text-[11px]' : 'text-xs sm:text-sm'} mt-0.5 truncate`}>
                            {special.away_team} @ {special.home_team}
                            <span className="mx-1.5 text-[#666]">·</span>
                            <span className="text-white font-semibold">{subhead}</span>
                        </p>
                    </div>

                    {/* Odds chip + CTA */}
                    <div className="shrink-0 flex flex-col items-end gap-1.5">
                        <span
                            className={`inline-flex items-center justify-center font-mono font-black ${compact ? 'px-2.5 py-1 text-sm' : 'px-3 py-1.5 text-base'} rounded-lg ${special.odds > 0 ? 'text-[#39FF14] bg-[#39FF14]/15 border border-[#39FF14]/40' : 'text-[#FF3B30] bg-[#FF3B30]/15 border border-[#FF3B30]/40'}`}
                        >
                            {oddsLabel}
                        </span>
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#FF8C00] uppercase tracking-wider">
                            {t('daily_special.bet_now')} <ChevronRight className="w-3 h-3" />
                        </span>
                    </div>
                </div>
            </motion.button>
        </AnimatePresence>
    );
};

export default DailySpecialBanner;
